// Общие примитивы кэширования AssetLab: ключ локации и правило устаревания записи.
//
// Живёт вне модулей намеренно: и списки ассетов (viewer), и словари фильтров
// (filtersCache) кэшируются по одному и тому же принципу, а раньше ключ лежал в
// finder/model.ts — там его описание говорило только про offset и ассеты.

// Общая часть записи любого кэша: когда её положили и надо ли обновить в фоне.
export type CachedRecord = {
  // Момент, когда данные пришли из сети (Date.now()). Ставится в редьюсере — так у
  // кэша в памяти и у персиста один источник времени.
  storedAt?: number;
  // true — запись восстановлена из localStorage (прошлая сессия): показываем её
  // мгновенно, но обновляем в фоне (SWR).
  revalidate?: boolean;
};

// Сколько запись, полученная в этой сессии, считается свежей. Вкладка AssetLab живёт
// часами: без этого лимита пользователь весь день видел бы утренний снапшот, а
// подписанные ссылки на превью за это время успевают истечь.
export const SESSION_STALE_MS = 3 * 60 * 1000;

// Нужен ли записи фоновый рефетч. Записи без storedAt (совсем старый формат персиста)
// считаем свежими — их и так помечает revalidate при гидрации.
export function isCacheStale(record: CachedRecord, now: number = Date.now()): boolean {
  if (record.revalidate) {
    return true;
  }
  if (record.storedAt === undefined) {
    return false;
  }
  return now - record.storedAt > SESSION_STALE_MS;
}

// Ключ кэша строится из всех параметров, влияющих на результат выборки, КРОМЕ offset
// (offset задаёт лишь страницу внутри одной и той же локации). Ключи сортируем и
// выбрасываем undefined, чтобы один и тот же набор фильтров всегда давал одну строку.
export function buildCacheKey(params: Record<string, unknown>): string {
  const ordered = Object.keys(params)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      const value = params[key];
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
  return JSON.stringify(ordered);
}
